<template>
  <div>
    <MyModal :width="'50rem'" :height="'38rem'" :isOpen="isOpen" :toggleModal="toggleModal" :zIndex="200" :id="'product-modal'">
        <div v-if="product?.name" class="font-inter relative product-modal overflow-auto pb-4 md:pb-0" @scroll="handleModalScroll($event)">
          <div class="px-4 sticky flex items-center" style="z-index:50" id="product-title-nav">
            <button class="transition duration-200 hover:text-primary p-2 rounded-full box-shadow bg-white justify-self-start left-4" @click="toggleModal(false)">
              <svg viewBox="0 0 24 24" role="presentation" style="width: 1.5rem; height: 1.5rem;"><path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" style="fill: currentcolor;"></path></svg>
            </button>
            <h2 class="opacity-0 font-semibold left-1/2 -translate-x-1/2 absolute only-1-lines min-w-64 text-center">{{ product.name }}</h2>
          </div>
          <div>
            <div class="grid grid-cols-12 gap-4">
              <div class="col-span-12 md:col-span-5 w-full pl-8 overflow-auto max-h-full main-image-container relative">
                <img 
                    class="rounded-xl h-60 w-full object-contain main-product-image" 
                    :src="product.media?.length ? (apiBaseUrl + '/storage/' + product.media[0].path) : '/assets/no-image.png'" 
                    alt="Imagem do produto" 
                    @error="($event.target as HTMLImageElement).src = '/assets/no-image.png'"
                >
                <button class="absolute top-4 right-4 box-shadow p-2 rounded-lg" style="background:rgba(255, 255, 255, 0.8);z-index:201" @click="isAmplifyImageModalOpen = true">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M21 9V3H15" stroke="#171717" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="stroke-primary" />
                    <path d="M3 15V21H9" stroke="#171717" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="stroke-primary" />
                    <path d="M21 3L13.5 10.5" stroke="#171717" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="stroke-primary" />
                    <path d="M10.5 13.5L3 21" stroke="#171717" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="stroke-primary" />
                  </svg>
                </button>
              </div>
              <div class="col-span-12 md:col-span-7 flex flex-col product-modal__right">
                <div class="px-4 pb-4">
                  <h5 class="text-xl font-bold" id="product-modal__product-name">{{ product.name }}</h5>
                  <p class="text-gray-500 max-w-full mb-4 mt-2 text-sm">
                      {{ displayDescription(product.description as string) }} 
                      <button v-if="!knowMore && (product.description?.length ?? 0) > 184" @click="knowMore = true" class="text-primary font-semibold uppercase underline">saiba mais</button>
                  </p>
                  
                  <!-- Price display -->
                  <div v-if="product.promoPrice" class="flex flex-col gap-1">
                    <p class="font-bold text-sm"><span>De: </span><span class="line-through text-red-400">{{ product.price.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}</span></p>
                    <p class="font-bold">
                      <span class="text-md">Por: </span> <span class="text-success">{{ product.promoPrice.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}</span>
                    </p>
                  </div>  
                  <p v-else class="font-bold text-neutral-700">{{ product.price.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) }}</p>
                </div>

                <!-- Variant Groups -->
                <div class="product-modal__right__variation" v-for="group in product.variantGroups" :key="group.id">
                  <div class="p-4 sticky top-0 z-20 product-modal__right__variation__header" style="background:#F9F9F9;">
                    <div class="flex justify-between items-center">
                      <h6 class="font-semibold">{{ group.name }}</h6> 
                      <div class="relative" v-if="group.isRequired && isVariantGroupFulfilled(group)">
                          <div class="h-4 w-4 rounded-full relative z-10 bg-success">
                              <svg viewBox="0 0 24 24" role="presentation" style="width: 1rem; height: 1rem;z-index:10"><path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z" style="fill: #fff;"></path></svg>
                          </div>
                      </div>
                      <span v-else-if="group.isRequired" class="text-white font-bold bg-black p-2 py-1 rounded-lg text-xs">obrigatório</span>
                    </div>
                    <div class="flex items-center gap-2 pt-1 text-xs text-gray-500">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                      <p v-if="group.min > 0">Selecione no mínimo {{ group.min }}<span v-if="group.max"> e no máximo {{ group.max }}</span></p>
                      <p v-else>Opcional <span v-if="group.max">(máximo {{ group.max }})</span></p>
                    </div>
                  </div>
                  <div class="grid grid-cols-12 gap-4 p-4">
                    <div 
                      v-for="variant in group.variants" 
                      :key="variant.id"
                      @click="toggleVariantSelection(group, variant)" 
                      class="relative cursor-pointer px-2 py-2 rounded-2xl font-bold transition-colors duration-200 w-full col-span-4 flex justify-center items-center flex-col gap-2 border hover:border-primary"
                      :class="isVariantSelected(variant.id) ? 'border-success bg-success/5' : 'border-gray-200'"
                    >
                      <img v-if="variant.media?.length" :src="apiBaseUrl + '/storage/' + variant.media[0].path" class="w-12 h-12 rounded-lg object-cover">
                      <span class="text-center text-sm" :class="isVariantSelected(variant.id) ? 'text-success' : ''">{{ variant.name }}</span>
                      <div v-if="variant.price > 0" class="px-2 py-0.5 bg-red-500 rounded-full text-white text-[10px] flex justify-center items-center">+{{ variant.price.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL'}) }}</div>
                    </div>
                  </div>
                </div>

                <!-- AddOn Groups -->
                <div class="product-modal__addon-groups__addon-group" v-for="group in product.addOnGroups" :key="group.id">
                  <div class="p-4 sticky top-0 product-modal__addon-groups__addon-group__header" style="background:#F9F9F9">
                    <div class="flex items-center justify-between gap-2">
                      <h6 class="font-semibold">{{ group.name }}</h6>
                      <div class="relative" v-if="group.isRequired && isAddOnGroupFulfilled(group)">
                          <div class="h-4 w-4 rounded-full relative z-10 bg-success">
                              <svg viewBox="0 0 24 24" role="presentation" style="width: 1rem; height: 1rem;z-index:10"><path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z" style="fill: #fff;"></path></svg>
                          </div>
                      </div>
                      <span v-else-if="group.isRequired" class="text-white font-bold bg-black p-2 py-1 rounded-lg text-xs">obrigatório</span>
                    </div>
                    <div class="flex items-center gap-2 pt-1 text-xs text-gray-500">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>
                        <div class="flex items-center justify-between w-full">
                            <p v-if="group.min > 0">Selecione no mínimo {{ group.min }}<span v-if="group.max"> e no máximo {{ group.max }}</span></p>
                            <p v-else>Opcional <span v-if="group.max">(máximo {{ group.max }})</span></p>
                            <span class="font-bold" v-if="group.max"><span class="text-success">{{ getAddOnGroupCurrentQuantity(group.id) }}</span>/{{ group.max }}</span>
                        </div>
                    </div>
                  </div>
                  <div class="flex flex-col">
                    <div v-for="addon in group.addOns" :key="addon.id" class="w-full flex items-center justify-between p-4 border-b last:border-0 hover:bg-gray-50 transition">
                      <div class="flex items-center gap-3">
                        <img 
                            class="h-14 w-14 rounded-xl object-cover border" 
                            :src="addon.media?.length ? (apiBaseUrl + '/storage/' + addon.media[0].path) : '/assets/no-image.png'" 
                            alt="Complemento"
                            @error="($event.target as HTMLImageElement).src = '/assets/no-image.png'"
                        >
                        <div>
                          <h6 class="text-sm font-semibold text-gray-700">{{ addon.name }}</h6>
                          <span v-if="addon.price > 0" class="text-success text-xs font-bold font-inter">+{{ addon.price.toLocaleString('pt-BR', {style: 'currency', currency: 'BRL'}) }}</span>
                        </div>
                      </div>
                      <div class="flex gap-4 items-center bg-gray-100 p-1 px-3 rounded-full border border-gray-200">
                        <button class="text-gray-500 hover:text-red-500 transition" @click="updateAddOnQuantity(group, addon, -1)">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M5 12h14"/></svg>
                        </button>
                        <span class="w-4 text-center font-bold text-sm">{{ getAddOnQuantity(addon.id) }}</span>
                        <button class="text-gray-500 hover:text-success transition" @click="updateAddOnQuantity(group, addon, 1)">
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M12 5v14M5 12h14"/></svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- Notes -->
            <div class="p-4 border-t">
              <label class="block text-sm font-semibold text-gray-700 mb-2">Observações (opcional)</label>
              <textarea
                v-model="notes"
                placeholder="Ex: Sem cebola, sem pimenta..."
                class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-primary resize-none"
                rows="3"
              ></textarea>
            </div>
          </div>

          <div class="flex items-center gap-4 pt-4 fixed bottom-0 box-shadow sm:shadow-none sm:mt-5 right-0 left-0 rounded-tl-2xl rounded-tr-2xl sm:rounded-2xl justify-between px-8 py-4 product-modal__add-to-cart bg-white border-t sm:border-none">
            <div class="flex gap-6 items-center bg-gray-100 p-2 px-6 rounded-full">
                <button class="text-gray-600 hover:text-primary transition" @click="quantity > 1 ? quantity-- : null">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M5 12h14"/></svg>
                </button>
                <span class="text-lg font-bold min-w-[1rem] text-center">{{ quantity }}</span>
                <button class="text-gray-600 hover:text-primary transition" @click="quantity++">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M12 5v14M5 12h14"/></svg>
                </button>
            </div>
            <button 
                :disabled="!canAddToCart" 
                @click="addToCart" 
                class="flex-1 max-w-xs flex items-center justify-between p-4 rounded-2xl transition duration-200 font-inter box-shadow" 
                :style="{background: canAddToCart ? organizationSettings.primaryColor || '#323232' : '#f5f5f5', color: canAddToCart ? '#fff' : '#949494'}"
            >
              <div class="text-left">
                <p class="text-[10px] uppercase font-bold opacity-80">Adicionar ao carrinho</p>
                <p class="text-lg font-bold">{{ totalPrice.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL'}) }}</p>
              </div>
              <svg viewBox="0 0 24 24" role="presentation" style="width: 1.8rem; height: 1.8rem;"><path d="M8.59,16.58L13.17,12L8.59,7.41L10,6L16,12L10,18L8.59,16.58Z" fill="currentColor"></path></svg>
            </button>
          </div>
        </div>
    </MyModal>

    <!-- Amplify Image Modal -->
    <MyModal :width="'100%'" :height="'100%'" :id="'amplify-image-modal'" :isOpen="isAmplifyImageModalOpen" :toggleModal="(v: any) => isAmplifyImageModalOpen = v" :zIndex="201">
      <div class="h-full flex flex-col bg-black/95"> 
        <button class="p-4 text-white hover:text-primary transition absolute top-4 left-4" @click="isAmplifyImageModalOpen = false">
          <svg viewBox="0 0 24 24" role="presentation" style="width: 2rem; height: 2rem;"><path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" fill="currentColor"></path></svg>
        </button>
        <div class="flex-1 flex flex-col justify-center items-center p-8 gap-6">
          <img 
            class="max-h-[70vh] object-contain rounded-2xl" 
            :src="product.media?.length ? (apiBaseUrl + '/storage/' + product.media[0].path) : '/assets/no-image.png'" 
            alt="Produto"
          >
          <div class="max-w-2xl w-full text-white text-center">
            <h2 class="text-2xl font-bold mb-2">{{ product.name }}</h2>
            <p class="text-sm opacity-80">{{ product.description }}</p>
          </div>
        </div>
      </div>
    </MyModal>
  </div>
</template>

<script lang="ts" setup>
import MyModal from './MyModal.vue';
import { ref, watch, computed } from 'vue';
import { cart, apiBaseUrl, organization, organizationSettings } from '@/global/state/GlobalState';
import { useToast } from "vue-toastification";

const props = defineProps<{
    product: any, 
    toggleModal: (v: boolean) => void, 
    isOpen: boolean
}>()

const quantity = ref<number>(1)
const selectedVariants = ref<Record<number, number[]>>({}) // groupIndex -> variantIds
const selectedAddOns = ref<Record<number, number>>({}) // addonId -> quantity
const notes = ref<string>('')

const knowMore = ref<boolean>(false)
const isAmplifyImageModalOpen = ref<boolean>(false)
const toast = useToast()

function displayDescription(description?: string){
    if(!description) return ''
    if(description.length > 184 && !knowMore.value){
        return description.substring(0, 164) + '...'
    }
    return description
}

function handleModalScroll(e: any){
    const productTitleNav = document.querySelector('#product-title-nav') as HTMLElement
    const modalProductTitle = document.querySelector('#product-modal__product-name') as HTMLElement
    if(productTitleNav && modalProductTitle){
        if(e.target.scrollTop >= modalProductTitle.offsetTop){
            productTitleNav.classList.add('show-title')
        } else{
            productTitleNav.classList.remove('show-title')
        }
    }
}

// Reset state when product changes
watch(() => props.product, () => {
    quantity.value = 1
    selectedVariants.value = {}
    selectedAddOns.value = {}
    notes.value = ''
    knowMore.value = false
}, { deep: true })

const isVariantSelected = (variantId: number) => {
    return Object.values(selectedVariants.value).some(ids => ids.includes(variantId))
}

const toggleVariantSelection = (group: any, variant: any) => {
    const groupId = group.id
    if (!selectedVariants.value[groupId]) {
        selectedVariants.value[groupId] = []
    }

    const currentSelection = selectedVariants.value[groupId]
    const isSelected = currentSelection.includes(variant.id)

    if (isSelected) {
        selectedVariants.value[groupId] = currentSelection.filter(id => id !== variant.id)
    } else {
        if (group.type === 'single' || group.max === 1) {
            selectedVariants.value[groupId] = [variant.id]
        } else {
            if (!group.max || currentSelection.length < group.max) {
                selectedVariants.value[groupId].push(variant.id)
            } else {
                toast.warning(`Máximo de ${group.max} opções permitido`)
            }
        }
    }
}

const getAddOnQuantity = (addonId: number) => selectedAddOns.value[addonId] || 0

const getAddOnGroupCurrentQuantity = (groupId: number) => {
    const group = props.product.addOnGroups.find((g: any) => g.id === groupId)
    if (!group) return 0
    return group.addOns.reduce((acc: number, addon: any) => acc + getAddOnQuantity(addon.id), 0)
}

const updateAddOnQuantity = (group: any, addon: any, delta: number) => {
    const currentQty = getAddOnQuantity(addon.id)
    const newQty = currentQty + delta
    
    if (newQty < 0) return

    const totalInGroup = getAddOnGroupCurrentQuantity(group.id)
    if (delta > 0 && group.max && totalInGroup >= group.max) {
        toast.warning(`Máximo de ${group.max} itens permitido para ${group.name}`)
        return
    }

    if (newQty === 0) {
        delete selectedAddOns.value[addon.id]
    } else {
        selectedAddOns.value[addon.id] = newQty
    }
}

const isVariantGroupFulfilled = (group: any) => {
    const selected = selectedVariants.value[group.id] || []
    return selected.length >= (group.min || (group.isRequired ? 1 : 0))
}

const isAddOnGroupFulfilled = (group: any) => {
    const total = getAddOnGroupCurrentQuantity(group.id)
    return total >= (group.min || (group.isRequired ? 1 : 0))
}

const canAddToCart = computed(() => {
    if (!props.product) return false
    
    const variantGroupsOk = props.product.variantGroups?.every(isVariantGroupFulfilled) ?? true
    const addOnGroupsOk = props.product.addOnGroups?.every(isAddOnGroupFulfilled) ?? true
    
    return variantGroupsOk && addOnGroupsOk && quantity.value > 0
})

const totalPrice = computed(() => {
    if (!props.product) return 0
    
    let basePrice = props.product.promoPrice || props.product.price
    let extras = 0

    // Add selected variants prices
    Object.values(selectedVariants.value).forEach(ids => {
        ids.forEach(id => {
            const variant = props.product.variantGroups.flatMap((g: any) => g.variants).find((v: any) => v.id === id)
            if (variant) extras += variant.price
        })
    })

    // Add selected addons prices
    Object.entries(selectedAddOns.value).forEach(([id, qty]) => {
        const addonId = parseInt(id)
        const addon = props.product.addOnGroups.flatMap((g: any) => g.addOns).find((a: any) => a.id === addonId)
        if (addon) extras += addon.price * qty
    })

    return (basePrice + extras) * quantity.value
})

function addToCart() {
    if (!canAddToCart.value) return

    // Format selection for the cart
    const cartItem = {
        ...props.product,
        cartId: Date.now() + Math.random(), // unique ID for item in cart
        quantity: quantity.value,
        selectedVariants: { ...selectedVariants.value },
        selectedAddOns: { ...selectedAddOns.value },
        notes: notes.value,
        totalPrice: totalPrice.value,
        unitPrice: totalPrice.value / quantity.value
    }

    cart.value.push(cartItem)
    toast.success('Produto adicionado!')
    props.toggleModal(false)
}
</script>

<style lang="scss">
.box-shadow {
  box-shadow: 0 4px 15px -3px rgb(0 0 0 / 0.1);
}
.only-1-lines {
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
</style>